import commonMutations from '@/store/data/common/mutations'
import { cloneDeep, find, findIndex, isEmpty, toNumber } from 'lodash'
import Vue from 'vue'

const mutations = {
    ...commonMutations,
    setGeneralSetting(state, { type, activate }) {
        Vue.set(state.general, type, activate)
    },
    setSocioDemoSetting(state, { type, activate }) {
        Vue.set(state.sociodemo, type, activate)
    },
    setDataMiningSetting(state, { type, activate }) {
        Vue.set(state.dataMining, type, activate)
    },

    // extra-taxo
    setTotalActiveTaxos(state, { activeTaxo }) {
        Vue.set(state.extraTaxo, 'activeTaxo', activeTaxo)
    },
    setExtraTaxos(state, { items }) {
        Vue.set(state.extraTaxo, 'list', cloneDeep(items))
    },
    setExtraTaxoIsLoading(state, { isLoading }) {
        Vue.set(state.extraTaxo, 'isLoading', isLoading)
    },

    // sharing
    setSharingTaxo(state, { linkedAccounts }) {
        Vue.set(state.sharing, 'taxonomies', linkedAccounts)
    },
    setSharingAudiences(state, { linkedAccounts }) {
        Vue.set(state.sharing, 'audiences', linkedAccounts)
    },
    setDefaultActiveSharing(state, { alreadyActiveSharing }) {
        Vue.set(state.sharing, 'activeSharing', alreadyActiveSharing)
    },
    updateSharings(state, { sharingType, addedSharings, deletedSharings }) {
        //update all sharings in store that went from inactive to active
        for (const added of addedSharings) {
            const sharingToUpdate = find(state.sharing[sharingType], sharing => sharing.id == added)
            if (sharingType === 'taxonomies') sharingToUpdate.wam_custom_taxonomy_sharing = 'active'
            else if (sharingType === 'audiences') sharingToUpdate.audience_sharing = 'active'
            const sharingToUpdateIndex = findIndex(state.sharing[sharingType], sharing => sharing.id == added)

            Vue.set(state.sharing[sharingType], sharingToUpdateIndex, sharingToUpdate)
        }

        //update all sharings in store that went from active to inactive
        for (const deleted of deletedSharings) {
            const sharingToUpdate = find(state.sharing[sharingType], sharing => sharing.id == deleted)
            if (sharingType === 'taxonomies') sharingToUpdate.wam_custom_taxonomy_sharing = 'inactive'
            else if (sharingType === 'audiences') sharingToUpdate.audience_sharing = 'inactive'
            const sharingToUpdateIndex = findIndex(state.sharing[sharingType], sharing => sharing.id == deleted)

            Vue.set(state.sharing[sharingType], sharingToUpdateIndex, sharingToUpdate)
        }
    },

    /**
     * Set the retrieved taxonomies shared, and place them in an array relative to their taxoId
     * @param {Object} sharedTaxonomies - List of shared taxonomies.
     */
    setSharingTaxonomyStatus(state, { sharedTaxonomies }) {
        const list = {}
        for (const sharedTaxo of sharedTaxonomies) {
            if (isEmpty(list[sharedTaxo.wam_custom_taxonomy_id])) {
                list[sharedTaxo.wam_custom_taxonomy_id] = [sharedTaxo]
            } else {
                list[sharedTaxo.wam_custom_taxonomy_id].push(sharedTaxo)
            }
        }
        Vue.set(state.sharing, 'sharedTaxonomies', list)
    },

    /**
     * Set the retrieved business apps list
     * @param {Object} payload
     * @param {Array} payload.list - List of business apps
     */
    setBusinessAppsList(state, { list }) {
        Vue.set(state.businessApps, 'list', list)
    },

    /**
     * Set the retrieved business apps list
     * @param {Object} payload
     * @param {Array} payload.list - List of business apps for a product account
     */
    setProductAccountBusinessAppsList(state, { list }) {
        Vue.set(state.businessApps, 'productAccountList', list)
    },

    /**
     * Update the status of a defined product account business app item
     * @param {Object} payload
     * @param {Array} payload.id - Id of the item to update
     * @param {Array} payload.element - New element to set
     * @param {Array} payload.action - Action to apply with the element (add or remove)
     */
    updateProductAccountBusinessAppStatus(state, { id, element, action }) {
        if (action === 'add') {
            const currentList = state.businessApps.productAccountList
            currentList.push(element)
            Vue.set(state.businessApps, 'productAccountList', currentList)
        } else if (action === 'remove') {
            const itemIndex = findIndex(state.businessApps.productAccountList, item => toNumber(item.id) === toNumber(id))
            const list = state.businessApps.productAccountList
            list.splice(itemIndex, 1)
            Vue.set(state.businessApps, 'productAccountList', list)
        }
    },

    /**
     * Update the status of a defined product account business app item when it's linked
     * @param {Object} payload
     * @param {Array} payload.element - New element to set
     */
    updateProductAccountBusinessAppStatusLink(state, { element }) {
        const currentList = state.businessApps.productAccountList
        currentList.push(element)
        Vue.set(state.businessApps, 'productAccountList', currentList)
    },

    /**
     * Update the status of a defined product account business app item when it's unlinked
     * @param {Object} payload
     * @param {Array} payload.id - Id of the item to update
     */
    updateProductAccountBusinessAppStatusUnlink(state, { id }) {
        const itemIndex = findIndex(state.businessApps.productAccountList, item => toNumber(item.id) === toNumber(id))
        const list = state.businessApps.productAccountList
        list.splice(itemIndex, 1)
        Vue.set(state.businessApps, 'productAccountList', list)
    },

    setSelectedBusinessApp(state, { businessApp }) {
        Vue.set(state.businessApps, 'selectedBusinessApp', businessApp)
    }
}

export default mutations
